import React, { useState, useEffect } from "react";
import { motion } from "framer-motion"; // Import Framer Motion
import { testimonialsTwo, paginationImages } from "../../data/testimonial.js";

export default function Testimonials({ backgroundComponent }) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [animationDirection, setAnimationDirection] = useState("initial"); // Track animation direction
  const [isFirstRender, setIsFirstRender] = useState(true); // Track if it's the first render

  useEffect(() => {
    // After the first render, set the flag to false
    setIsFirstRender(false);
  }, []);

  const handlePaginationClick = (index) => {
    if (currentSlideIndex !== index) {
      setAnimationDirection(index > currentSlideIndex ? "right" : "left");
      setCurrentSlideIndex(index);
    }
  };

  // Framer Motion animation variants for text and author info
  const slideVariants = {
    initial: { opacity: 0, y: 50 }, // Slide in from the bottom (initial load)
    right: { opacity: 0, x: 50 }, // Slide in from the right (next slide)
    left: { opacity: 0, x: -50 }, // Slide in from the left (previous slide)
    visible: { opacity: 1, x: 0, y: 0, transition: { duration: 0.6 } }, // Animate to original position
  };

  return (
    <section className="layout-pt-md layout-pb-md relative">
      {/* Background */}
      <div
        className={`absolute top-0 left-0 w-full h-full ${
          backgroundComponent ? "bg-white-two" : "bg-light-5"
        }`}
      ></div>

      <div className="relative z-10 bg-gray-200 rounded-lg max-w-4xl lg:max-w-[1570px] mx-auto">
        <div className="p-8 lg:p-12">
          {/* Title Section */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Testimonials</h2>
            <p className="text-lg text-gray-600">
              Hear from NEET Achievers with NEET Support!
            </p>
          </div>

          {/* Testimonial Content */}
          <div className="max-w-6xl mx-auto relative p-8 overflow-hidden">
            {/* Quote Icon */}
            <div className="absolute top-1 left-3 -translate-x-2 -translate-y-2">
              <img
                src="assets/misc/quote.svg"
                alt="quote"
                className="h-14 w-14"
              />
            </div>

            {/* Sliding Content */}
            <div className="relative">
              {/* Testimonial Text */}
              <motion.p
                className="text-lg lg:text-2xl font-medium text-[#140342] mt-3 sm:mt-0 pl-0 md:pl-16 leading-relaxed mb-10"
                key={currentSlideIndex} // Key is important for Framer Motion to re-render on change
                initial={isFirstRender ? "initial" : animationDirection} // Dynamic initial animation
                animate="visible"
                variants={slideVariants}
              >
                {testimonialsTwo[currentSlideIndex].text}
              </motion.p>

              {/* Author Info */}
              <motion.div
                className="text-center"
                key={`author-${currentSlideIndex}`}
                initial={isFirstRender ? "initial" : animationDirection} // Dynamic initial animation
                animate="visible"
                variants={slideVariants}
              >
                <p className="text-lg font-semibold text-gray-900">
                  {testimonialsTwo[currentSlideIndex].author}
                </p>
                <p className="text-sm text-gray-500">
                  {testimonialsTwo[currentSlideIndex].position}
                </p>
              </motion.div>
            </div>
          </div>

          {/* Pagination with Images */}
          <div className="pt-8 flex justify-center gap-2 md:gap-10">
            {paginationImages.map((elm, index) => (
              <button
                key={index}
                onClick={() => handlePaginationClick(index)}
                className={`w-16 h-16 lg:w-24 lg:h-24 rounded-full overflow-hidden p-1 lg:p-2 transition-all ${
                  currentSlideIndex === index
                    ? "ring-2 ring-[#ff9245]" // Show ring on active
                    : "ring-0" // No ring when inactive
                }`}
              >
                <img
                  src={elm}
                  alt={`Slide ${index + 1}`}
                  className="w-full h-full rounded-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
